declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

// GA4 + Google Ads etiketleri app/layout.tsx'te Consent Mode v2 ile yüklenir.
// Varsayılan durum "denied"dır; aşağıdaki fonksiyonlar kullanıcı kararını etiketlere bildirir.
//
// ÖNEMLİ: Bunlar eskiden no-op olan initGA4()'ün yerini alır. Eski hâlde "Reddet"
// butonu hiçbir şey yapmıyordu — GA4 ve Ads zaten koşulsuz çalışıyordu (KVKK riski).

/** Consent Mode v2'nin dört sinyali. */
type ConsentState = 'granted' | 'denied'

function updateConsent(state: ConsentState) {
  if (!isGA4Ready()) return
  window.gtag('consent', 'update', {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  })
}

/** Kullanıcı çerezleri KABUL etti — ölçüm ve reklam sinyallerini aç. */
export function grantConsent() {
  updateConsent('granted')
}

/** Kullanıcı çerezleri REDDETTİ — sinyalleri kapalı tut ve mevcut GA çerezlerini sil. */
export function denyConsent() {
  updateConsent('denied')
  if (typeof document === 'undefined') return
  // Reddedildikten sonra daha önce yazılmış _ga* çerezleri kalmamalı.
  const host = window.location.hostname
  const domains = [host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`]
  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0]?.trim()
    if (!name || !/^(_ga|_gid|_gcl|_gat)/.test(name)) continue
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`
    }
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
  }
}

export function isGA4Ready(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function'
}

/* ─── Page View (SPA navigation) ─── */
export function trackPageView(path: string) {
  if (!isGA4Ready()) return
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: document.title,
    page_location: window.location.origin + path,
  })
}

/* ─── Generic Event ─── */
export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (!isGA4Ready()) return
  window.gtag('event', eventName, params)
}

/* ─── Core Web Vitals → GA4 ─── */
export function trackWebVital(name: string, value: number, id?: string) {
  if (!isGA4Ready()) return
  window.gtag('event', name, {
    value: Math.round(name === 'CLS' ? value * 1000 : value),
    event_category: 'Web Vitals',
    event_label: id,
    non_interaction: true,
  })
}

/* ─── Search ─── */
export function trackSearch(query: string, resultCount: number) {
  if (!isGA4Ready()) return
  window.gtag('event', 'search', {
    search_term: query,
    results_count: resultCount,
  })
}

/* ─── Outbound Link Click ─── */
export function trackOutboundClick(url: string, label?: string) {
  if (!isGA4Ready()) return
  window.gtag('event', 'outbound_click', {
    link_url: url,
    link_text: label || url,
    event_category: 'outbound',
    transport_type: 'beacon',
  })
}

/* ─── Affiliate / Broker Click ─── */
export function trackAffiliateClick(
  brokerId: string,
  brokerName: string,
  source: string,
  destinationUrl?: string,
) {
  if (!isGA4Ready()) return
  window.gtag('event', 'affiliate_click', {
    broker_id: brokerId,
    broker_name: brokerName,
    source,
    link_url: destinationUrl,
    event_category: 'conversion',
    transport_type: 'beacon',
  })
}

/* ─── Theme Change ─── */
export function trackThemeChange(theme: string) {
  if (!isGA4Ready()) return
  window.gtag('event', 'theme_change', {
    theme,
    event_category: 'engagement',
  })
}

/* ─── Language Change ─── */
export function trackLanguageChange(from: string, to: string) {
  trackEvent('language_change', {
    from_language: from,
    to_language: to,
    event_category: 'engagement',
  })
}

/* ─── Scroll Depth (25%, 50%, 75%, 90%) ─── */
export function setupScrollTracking(): (() => void) | undefined {
  if (typeof window === 'undefined') return
  const milestones = [25, 50, 75, 90]
  const reached = new Set<number>()

  const handler = () => {
    const total = document.body.scrollHeight - window.innerHeight
    if (total <= 0) return
    const percent = Math.round((window.scrollY / total) * 100)
    for (const m of milestones) {
      if (percent >= m && !reached.has(m)) {
        reached.add(m)
        trackEvent('scroll_depth', { percent: m, event_category: 'engagement' })
      }
    }
  }

  window.addEventListener('scroll', handler, { passive: true })
  return () => window.removeEventListener('scroll', handler)
}

/* ─── Market / Instrument View ─── */
export function trackMarketView(symbol: string, type: 'stock' | 'crypto' | 'forex' | 'index' | 'commodity') {
  trackEvent('view_item', {
    item_id: symbol,
    item_category: type,
    content_type: 'market_instrument',
  })
}

/* ─── News Article View ─── */
export function trackNewsView(slug: string, title: string, category?: string) {
  trackEvent('view_item', {
    item_id: slug,
    item_name: title,
    item_category: category || 'news',
    content_type: 'article',
  })
}

/* ─── Broker Click ─── */
export function trackBrokerClick(brokerId: string, brokerName: string) {
  trackEvent('broker_click', {
    broker_id: brokerId,
    broker_name: brokerName,
    event_category: 'conversion',
  })
}

/* ─── Time on Page ─── */
export function setupTimeOnPage(): (() => void) | undefined {
  if (typeof window === 'undefined') return
  const start = Date.now()
  const handler = () => {
    if (document.visibilityState === 'hidden') {
      const seconds = Math.round((Date.now() - start) / 1000)
      trackEvent('time_on_page', {
        value: seconds,
        event_category: 'engagement',
        non_interaction: true,
      })
    }
  }
  document.addEventListener('visibilitychange', handler)
  return () => document.removeEventListener('visibilitychange', handler)
}
